import * as React from 'react';
import axios from 'axios';
import { getCookies } from "cookies-next";
import { config } from '../../data/config';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { Input } from 'rsuite';

const DEFAULT_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

type LanguageCode = string;

const LANG_CODES: LanguageCode[] = ['en', 'fa', 'ar', 'ku', 'tr'];

// Helpers for multi-language text (label can be string for legacy or object { en, fa, ar, ... })
const getMultilingualValue = (field: any, lang: LanguageCode, fallback: string = ''): string => {
    if (!field) return fallback;
    if (typeof field === 'string') return field;
    if (typeof field === 'object') {
        return field[lang] || field.en || field.fa || field.ar || field.ku || field.tr || fallback;
    }
    return fallback;
};

const setMultilingualValue = (currentData: any, lang: LanguageCode, value: string): any => {
    if (!currentData || typeof currentData !== 'object') {
        return { [lang]: value };
    }
    return { ...currentData, [lang]: value };
};

export default function TissCounter({ data, reload, activeLang = 'en' }: { data: any; reload: () => void; activeLang?: LanguageCode }) {
    const { t } = useTranslation();
    let cookie = getCookies();

    const initialList = data.data?.list || [];
    const hasContent = initialList.length > 0;

    const [isEditing, setIsEditing] = React.useState(!hasContent);
    const [list, setList] = React.useState<any[]>(initialList.length > 0 ? initialList : []);
    const [text, setText] = React.useState<string>('');
    const [saving, setSaving] = React.useState(false);
    const [hasChanges, setHasChanges] = React.useState(false);
    const [editingItem, setEditingItem] = React.useState<number | null>(null);
    const [editingField, setEditingField] = React.useState<'text' | 'number' | null>(null);
    const [translating, setTranslating] = React.useState(false);

    // Translate label text: try backend first, then Google fallback
    const translateText = async (text: string, fromLang: LanguageCode, toLang: LanguageCode): Promise<string> => {
        if (!text || String(text).trim() === '') return '';
        const from = fromLang === 'krd' ? 'ku' : fromLang;
        const to = toLang === 'krd' ? 'ku' : toLang;
        if (from === to) return text;
        try {
            try {
                const response = await axios.post(
                    `${config.url}/v1/translate`,
                    { text: text.trim(), from, to, format: 'text' },
                    { headers: { Authorization: 'Bearer ' + cookie['token'] } }
                );
                if (response.data?.translatedText) return response.data.translatedText;
            } catch (_) {
                // Backend not available, use Google
            }
            const response = await fetch(
                `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${from}&tl=${to}&dt=t&q=${encodeURIComponent(text.trim())}`
            );
            if (!response.ok) return '';
            const result = await response.json();
            const translated = result[0]?.map((s: any) => s?.[0] || '').join('').trim();
            return translated || text;
        } catch (_) {
            return '';
        }
    };

    // Auto-translate all counter labels from activeLang to other languages
    const handleAutoTranslateLabels = async () => {
        const itemsWithLabel = list.filter((item) => (getMultilingualValue(item.text, activeLang) || '').trim());
        if (itemsWithLabel.length === 0) {
            Swal.fire({
                title: t('No content to translate'),
                text: t('Please add at least one counter label in the current language first'),
                icon: 'warning',
            });
            return;
        }
        const confirmed = await Swal.fire({
            title: t('Auto translate'),
            text: t('This will translate all counter labels to the other languages. Continue?'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('Yes, translate'),
            cancelButtonText: t('Cancel'),
        });
        if (!confirmed.isConfirmed) return;

        setTranslating(true);
        try {
            const sourceLang = activeLang === 'krd' ? 'ku' : activeLang;
            const targetLangs = LANG_CODES.filter((l) => l !== sourceLang);
            const updatedList = await Promise.all(
                list.map(async (item) => {
                    const sourceText = (getMultilingualValue(item.text, activeLang) || '').trim();
                    if (!sourceText) return item;
                    const newText = { ...(typeof item.text === 'object' && item.text ? item.text : { [activeLang]: item.text || '' }) };
                    for (const toLang of targetLangs) {
                        const translated = await translateText(sourceText, activeLang, toLang);
                        if (translated) newText[toLang] = translated;
                        await new Promise((r) => setTimeout(r, 120));
                    }
                    return { ...item, text: newText };
                })
            );
            setList(updatedList);
            setHasChanges(true);
            Swal.fire({
                title: t('Translation completed'),
                text: t('Labels have been translated to other languages'),
                icon: 'success',
                timer: 2000,
                showConfirmButton: false,
            });
        } catch (err: any) {
            Swal.fire({
                title: t('Translation failed'),
                text: err?.message || t('An error occurred during translation'),
                icon: 'error',
            });
        } finally {
            setTranslating(false);
        }
    };
    React.useEffect(() => {
        const dataEqual = JSON.stringify(list) === JSON.stringify(initialList);
        setHasChanges(!dataEqual);
    }, [list, initialList]);

    const addItem = () => {
        if (!text.trim()) {
            Swal.fire({
                title: t('Error'),
                text: t('Please enter a label'),
                icon: 'warning',
            });
            return;
        }

        const newItem = {
            text: { [activeLang]: text.trim() },
            numberitem: 0,
            coloritem: DEFAULT_COLORS[list.length % DEFAULT_COLORS.length],
            arrange: list.length + 1,
        };
        setList([...list, newItem]);
        setText('');
    };

    const removeItem = async (index: number) => {
        const result = await Swal.fire({
            title: t('Delete counter?'),
            text: t('This action cannot be undone'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete'),
            cancelButtonText: t('Cancel'),
        });

        if (result.isConfirmed) {
            const updatedList = list.filter((_, i) => i !== index).map((item, idx) => ({
                ...item,
                arrange: idx + 1,
            }));
            setList(updatedList);
        }
    };

    const moveItem = (index: number, direction: 'up' | 'down') => {
        const newList = [...list];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        if (targetIndex >= 0 && targetIndex < newList.length) {
            [newList[index], newList[targetIndex]] = [newList[targetIndex], newList[index]];
            newList[index].arrange = index + 1;
            newList[targetIndex].arrange = targetIndex + 1;
            setList(newList);
        }
    };

    const editItem = (index: number, field: 'text' | 'number', value: string | number) => {
        const updatedList = list.map((item, idx) => {
            if (idx === index) {
                if (field === 'text') {
                    return { ...item, text: setMultilingualValue(item.text, activeLang, value as string) };
                } else {
                    return { ...item, numberitem: value as number };
                }
            }
            return item;
        });
        setList(updatedList);
    };

    const editItemColor = (index: number, color: string) => {
        const updatedList = list.map((item, idx) => 
            idx === index ? { ...item, coloritem: color } : item
        );
        setList(updatedList);
    };

    const save = async () => {
        if (!data?._id) return;

        setSaving(true);
        try {
            await axios.put(
                `${config.url}/v1/dashboard/component/texteditor/${data._id}`,
                { list },
                {
                    headers: {
                        Authorization: `Bearer ${cookie['token']}`,
                    },
                }
            );

            Swal.fire({
                title: t('Saved successfully'),
                icon: 'success',
                timer: 1200,
                showConfirmButton: false,
            });
            setIsEditing(false);
            setHasChanges(false);
            if (reload) reload();
        } catch (error: any) {
            console.error('Error saving data:', error);
            Swal.fire({
                title: t('Error saving'),
                icon: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        if (hasChanges) {
            Swal.fire({
                title: t('Discard changes?'),
                text: t('You have unsaved changes. Are you sure you want to cancel?'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: t('Yes, discard'),
                cancelButtonText: t('No, keep editing'),
            }).then((result) => {
                if (result.isConfirmed) {
                    setList(initialList);
                    setIsEditing(false);
                    setHasChanges(false);
                }
            });
        } else {
            setIsEditing(false);
        }
    };

    // Empty state
    if (!hasContent && !isEditing) {
        return (
            <div
                className="w-full p-4 flex flex-col items-center justify-center cursor-pointer hover:bg-blue-50/30 bg-white transition-all duration-300 group"
                onClick={() => setIsEditing(true)}
                dir="rtl"
            >
                <div className="bg-blue-100 rounded-full p-4 mb-4 group-hover:bg-blue-200 transition-colors duration-300">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                    </svg>
                </div>
                <span className="text-gray-600 text-sm font-medium group-hover:text-blue-600 transition-colors duration-300">
                    {t('Click to add counter')}
                </span>
                <span className="text-gray-400 text-xs mt-1">
                    {t('Create number counters with labels')}
                </span>
            </div>
        );
    }

    // Preview Mode
    if (!isEditing) {
        return (
            <div className="w-full bg-white transition-all duration-300 group relative" dir="rtl">
                <div className="p-0.5 relative">
                    {list.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 p-6">
                            {list.map((item, index) => (
                                <div
                                    key={index}
                                    className="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-white via-gray-50 to-blue-50 rounded-3xl border-2 border-gray-200 hover:border-blue-400 transition-all duration-300 hover:shadow-2xl group/item transform hover:scale-105"
                                >
                                    {/* Number Circle - Enhanced */}
                                    <div className="relative mb-6">
                                        <div
                                            className="relative w-36 h-36 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 group-hover/item:scale-110 group-hover/item:shadow-3xl"
                                            style={{
                                                backgroundColor: item.coloritem || DEFAULT_COLORS[index % DEFAULT_COLORS.length],
                                                boxShadow: `0 25px 50px -12px ${item.coloritem || DEFAULT_COLORS[index % DEFAULT_COLORS.length]}60, 0 0 0 4px rgba(255,255,255,0.1)`,
                                            }}
                                        >
                                            {/* Inner glow effect */}
                                            <div 
                                                className="absolute inset-0 rounded-full opacity-30 blur-xl"
                                                style={{
                                                    backgroundColor: item.coloritem || DEFAULT_COLORS[index % DEFAULT_COLORS.length],
                                                }}
                                            ></div>
                                            
                                            {/* Number Display */}
                                            <span className="relative text-6xl font-black text-white drop-shadow-2xl z-10">
                                                {item.numberitem || 0}
                                            </span>
                                            
                                            {/* Hover effects */}
                                            <div className="absolute inset-0 rounded-full bg-white/20 opacity-0 group-hover/item:opacity-100 transition-opacity duration-300"></div>
                                            <div className="absolute -inset-3 rounded-full border-4 border-white/40 opacity-0 group-hover/item:opacity-100 transition-opacity duration-300"></div>
                                            
                                            {/* Animated ring on hover */}
                                            <div className="absolute -inset-4 rounded-full border-2 border-blue-300 opacity-0 group-hover/item:opacity-100 transition-opacity duration-500 animate-ping"></div>
                                        </div>
                                        
                                        {/* Count badge */}
                                        <div className="absolute -top-2 -right-2 bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-lg border-2 border-white font-bold text-sm z-20">
                                            {index + 1}
                                        </div>
                                    </div>
                                    
                                    {/* Label */}
                                    <h3 className="text-xl font-bold text-gray-800 text-center mb-2 px-2">
                                        {getMultilingualValue(item.text, activeLang) || t('Counter')}
                                    </h3>
                                    
                                    {/* Decorative line */}
                                    <div className="w-20 h-1.5 bg-gradient-to-r from-blue-400 via-indigo-500 to-purple-500 rounded-full mt-2"></div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-16 text-gray-400">
                            <div className="flex flex-col items-center">
                                <div className="bg-gray-100 rounded-full p-6 mb-4">
                                    <svg className="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                                    </svg>
                                </div>
                                <p className="text-base font-medium">{t('No counters yet')}</p>
                            </div>
                        </div>
                    )}

                    {/* Edit Button - Appears on hover */}
                    {list.length > 0 && (
                        <div className="absolute top-3 left-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                            <button
                                onClick={() => setIsEditing(true)}
                                className="bg-white border-2 border-gray-300 rounded-xl px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-600 shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                {t('Edit Counters')}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Edit Mode
    return (
        <div className="w-full bg-white overflow-hidden transition-all duration-300" dir="rtl">
            <div className="p-0.5">
                <div className="space-y-4">
                    {/* Add New Item - label for current language (activeLang) */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border-2 border-blue-100">
                        <p className="text-xs text-gray-500 mb-2 text-right">{t('Label')} ({activeLang})</p>
                        <div className="flex gap-3">
                            <Input
                                value={text}
                                onChange={(value) => setText(value || '')}
                                placeholder={t('Enter counter label...')}
                                onPressEnter={addItem}
                                className="flex-1"
                                style={{
                                    padding: '10px 16px',
                                    border: '2px solid #e5e7eb',
                                    borderRadius: '10px',
                                    textAlign: 'right',
                                }}
                            />
                            <button
                                onClick={addItem}
                                className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center gap-2 text-sm font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                                </svg>
                                {t('Add Counter')}
                            </button>
                        </div>
                    </div>

                    {/* Live Preview */}
                    {list.length > 0 && (
                        <div className="bg-gradient-to-br from-white via-blue-50 to-indigo-50 rounded-2xl p-8 border-2 border-blue-200 shadow-2xl">
                            <div className="flex items-center justify-between mb-6 pb-5 border-b-2 border-blue-100">
                                <div className="flex items-center gap-4">
                                    <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-3 shadow-lg">
                                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900 text-right">{t('Live Preview')}</h3>
                                        <p className="text-sm text-gray-600 mt-1">{t('Numbers in circles - changes update instantly')}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 bg-gradient-to-r from-green-50 to-emerald-50 px-4 py-2 rounded-full border-2 border-green-200 shadow-sm">
                                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse shadow-lg"></div>
                                    <span className="text-sm text-green-700 font-bold">{t('Live')}</span>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                {list.map((item, index) => (
                                    <div
                                        key={index}
                                        className="flex flex-col items-center justify-center p-6 bg-white rounded-2xl border-2 border-gray-200 hover:border-blue-400 transition-all duration-300 hover:shadow-xl transform hover:scale-105 group/preview"
                                    >
                                        {/* Enhanced Circle with Number */}
                                        <div className="relative mb-4">
                                            <div
                                                className="relative w-32 h-32 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 group-hover/preview:scale-110"
                                                style={{
                                                    backgroundColor: item.coloritem || DEFAULT_COLORS[index % DEFAULT_COLORS.length],
                                                    boxShadow: `0 20px 40px -12px ${item.coloritem || DEFAULT_COLORS[index % DEFAULT_COLORS.length]}60, 0 0 0 3px rgba(255,255,255,0.2)`,
                                                }}
                                            >
                                                {/* Inner glow */}
                                                <div 
                                                    className="absolute inset-0 rounded-full opacity-25 blur-lg"
                                                    style={{
                                                        backgroundColor: item.coloritem || DEFAULT_COLORS[index % DEFAULT_COLORS.length],
                                                    }}
                                                ></div>
                                                
                                                {/* Number - Larger and more prominent */}
                                                <span className="relative text-5xl font-black text-white drop-shadow-2xl z-10">
                                                    {item.numberitem || 0}
                                                </span>
                                                
                                                {/* Hover overlay */}
                                                <div className="absolute inset-0 rounded-full bg-white/20 opacity-0 group-hover/preview:opacity-100 transition-opacity duration-300"></div>
                                                
                                                {/* Outer ring on hover */}
                                                <div className="absolute -inset-2 rounded-full border-2 border-white/50 opacity-0 group-hover/preview:opacity-100 transition-opacity duration-300"></div>
                                            </div>
                                            
                                            {/* Counter badge */}
                                            <div className="absolute -top-1 -right-1 bg-gradient-to-br from-purple-600 to-pink-600 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg border-2 border-white font-bold text-xs z-20">
                                                {index + 1}
                                            </div>
                                        </div>
                                        
                                        {/* Label */}
                                        <h4 className="text-base font-bold text-gray-800 text-center px-2">
                                            {getMultilingualValue(item.text, activeLang) || t('Counter')}
                                        </h4>
                                        
                                        {/* Decorative line */}
                                        <div className="w-16 h-1 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full mt-2"></div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Counter Items Editor */}
                    {list.length > 0 && (
                        <div className="bg-white rounded-xl p-5 border-2 border-gray-200 shadow-sm">
                            <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
                                <div className="flex items-center gap-3">
                                    <div className="bg-purple-100 rounded-lg p-2">
                                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-base font-bold text-gray-800 text-right">{t('Counters List')}</h3>
                                        <p className="text-xs text-gray-500 mt-0.5">{t('Manage your counters')}</p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleAutoTranslateLabels}
                                    disabled={translating || list.length === 0}
                                    title={t('Auto translate labels to other languages')}
                                    className="px-4 py-2 border border-indigo-300 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-all duration-200 flex items-center gap-2"
                                >
                                    {translating ? (
                                        <>
                                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            <span>{t('Translating...')}</span>
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                            </svg>
                                            {t('Auto translate labels')}
                                        </>
                                    )}
                                </button>
                            </div>
                            <div className="space-y-3">
                                {list.map((item, index) => (
                                    <div
                                        key={index}
                                        className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 border-2 border-gray-200 hover:border-blue-300 transition-all duration-300 group/item"
                                    >
                                        <div className="flex items-center gap-4">
                                            {/* Number Badge */}
                                            <div className="flex-shrink-0">
                                                <div className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg font-extrabold text-white text-lg bg-gradient-to-br from-gray-600 to-gray-700 border-2 border-white"
                                                    style={{
                                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                    }}
                                                >
                                                    {index + 1}
                                                </div>
                                            </div>

                                            {/* Preview Circle - Enhanced */}
                                            <div className="flex-shrink-0 relative">
                                                <div
                                                    className="relative w-28 h-28 rounded-full flex items-center justify-center shadow-xl transition-all duration-300 group-hover/item:scale-110 group-hover/item:shadow-2xl"
                                                    style={{
                                                        backgroundColor: item.coloritem || DEFAULT_COLORS[index % DEFAULT_COLORS.length],
                                                        boxShadow: `0 15px 30px -10px ${item.coloritem || DEFAULT_COLORS[index % DEFAULT_COLORS.length]}60, 0 0 0 3px rgba(255,255,255,0.15)`,
                                                    }}
                                                >
                                                    {/* Inner glow effect */}
                                                    <div 
                                                        className="absolute inset-0 rounded-full opacity-20 blur-md"
                                                        style={{
                                                            backgroundColor: item.coloritem || DEFAULT_COLORS[index % DEFAULT_COLORS.length],
                                                        }}
                                                    ></div>
                                                    
                                                    {/* Number Display */}
                                                    <span className="relative text-4xl font-black text-white drop-shadow-xl z-10">
                                                        {item.numberitem || 0}
                                                    </span>
                                                    
                                                    {/* Hover overlay */}
                                                    <div className="absolute inset-0 rounded-full bg-white/25 opacity-0 group-hover/item:opacity-100 transition-opacity duration-300"></div>
                                                    
                                                    {/* Outer ring */}
                                                    <div className="absolute -inset-2 rounded-full border-2 border-white/40 opacity-0 group-hover/item:opacity-100 transition-opacity duration-300"></div>
                                                </div>
                                                
                                                {/* Small number badge */}
                                                <div className="absolute -top-1 -right-1 bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-full w-7 h-7 flex items-center justify-center shadow-md border-2 border-white font-bold text-xs z-20">
                                                    {index + 1}
                                                </div>
                                            </div>

                                            {/* Form Fields */}
                                            <div className="flex-1 space-y-3">
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 text-right">
                                                        {t('Label')} ({activeLang})
                                                    </label>
                                                    {editingItem === index && editingField === 'text' ? (
                                                        <Input
                                                            value={getMultilingualValue(item.text, activeLang)}
                                                            onChange={(value) => editItem(index, 'text', value || '')}
                                                            onBlur={() => {
                                                                setEditingItem(null);
                                                                setEditingField(null);
                                                            }}
                                                            onPressEnter={() => {
                                                                setEditingItem(null);
                                                                setEditingField(null);
                                                            }}
                                                            autoFocus
                                                            style={{
                                                                padding: '8px 12px',
                                                                border: '2px solid #3b82f6',
                                                                borderRadius: '8px',
                                                                textAlign: 'right',
                                                            }}
                                                        />
                                                    ) : (
                                                        <div
                                                            onClick={() => {
                                                                setEditingItem(index);
                                                                setEditingField('text');
                                                            }}
                                                            className="px-4 py-2.5 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-400 cursor-pointer transition-colors text-gray-700 font-medium"
                                                        >
                                                            {getMultilingualValue(item.text, activeLang) ? getMultilingualValue(item.text, activeLang) : <span className="text-gray-400 italic">{t('Click to edit')}</span>}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-600 mb-1.5 text-right">
                                                            {t('Number')}
                                                        </label>
                                                        {editingItem === index && editingField === 'number' ? (
                                                            <Input
                                                                type="number"
                                                                value={item.numberitem || 0}
                                                                onChange={(value) => editItem(index, 'number', parseInt(value as string) || 0)}
                                                                onBlur={() => {
                                                                    setEditingItem(null);
                                                                    setEditingField(null);
                                                                }}
                                                                onPressEnter={() => {
                                                                    setEditingItem(null);
                                                                    setEditingField(null);
                                                                }}
                                                                autoFocus
                                                                style={{
                                                                    padding: '8px 12px',
                                                                    border: '2px solid #3b82f6',
                                                                    borderRadius: '8px',
                                                                    textAlign: 'right',
                                                                }}
                                                            />
                                                        ) : (
                                                            <div
                                                                onClick={() => {
                                                                    setEditingItem(index);
                                                                    setEditingField('number');
                                                                }}
                                                                className="px-4 py-2.5 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-400 cursor-pointer transition-colors text-gray-700 font-medium"
                                                            >
                                                                {item.numberitem || 0}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-600 mb-1.5 text-right">
                                                            {t('Color')}
                                                        </label>
                                                        <div className="relative group/color">
                                                            <input
                                                                type="color"
                                                                value={item.coloritem || DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
                                                                onChange={(e) => editItemColor(index, e.target.value)}
                                                                className="w-full h-11 rounded-lg border-2 border-gray-300 cursor-pointer hover:border-blue-500 hover:scale-105 transition-all shadow-md hover:shadow-lg"
                                                                title={t('Change color')}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="flex flex-col gap-2 flex-shrink-0">
                                                <button
                                                    onClick={() => moveItem(index, 'up')}
                                                    disabled={index === 0}
                                                    className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
                                                    title={t('Move up')}
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => moveItem(index, 'down')}
                                                    disabled={index === list.length - 1}
                                                    className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
                                                    title={t('Move down')}
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => removeItem(index)}
                                                    className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all shadow-md hover:shadow-lg"
                                                    title={t('Delete')}
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Empty State in Edit Mode */}
                    {list.length === 0 && (
                        <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-12 border-2 border-dashed border-blue-200 text-center">
                            <div className="bg-blue-100 rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                                <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                                </svg>
                            </div>
                            <p className="text-gray-700 font-semibold mb-2 text-base">{t('No counters yet')}</p>
                            <p className="text-sm text-gray-500 max-w-md mx-auto">{t('Add counters above to get started')}</p>
                        </div>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="mt-6 flex gap-3 justify-end items-center">
                    {hasChanges && (
                        <span className="text-xs text-orange-600 bg-orange-100 px-3 py-1.5 rounded-full font-medium">
                            {t('Unsaved changes')}
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={handleCancel}
                        disabled={saving}
                        className="px-5 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 text-sm font-medium text-gray-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        {t('Cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving || !hasChanges}
                        className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg"
                    >
                        {saving ? (
                            <>
                                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>{t('Saving...')}</span>
                            </>
                        ) : (
                            <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span>{t('Save')}</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
